import React, { useState, useRef, useEffect } from "react";
import PhoneNumberIcon from "@/helpers/svg/phoneNumberIcon.svg";
import ResendIcon from "@/helpers/svg/resendIcon.svg";
import ShowPasswordIcon from "@/helpers/svg/showPasswordIcon.svg";

import SmsIcon from "@/helpers/svg/smsIcon.svg";
import CustomButton from "@/components/common/button";
import OtpInput from "@/components/profile/otpInput";
import EditIcon from "@/helpers/svg/editIcon.svg";
import HourglassIcon from "@/helpers/svg/hourglassIcon.svg";
import LeftArrowIcon from "@/helpers/svg/leftArrowIcon.svg";
import LoginErrorIcon from "@/helpers/svg/loginErrorIcon.svg";
import {
  Input,
  Tab,
  Tabs,
  RadioGroup,
  Radio,
  Spinner,
  Button,
  Avatar,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
} from "@nextui-org/react";

const MAX_PROFILES = 3;

// ---------------------------------------------------------------------------
// Shared Glassmorphism styles
// ---------------------------------------------------------------------------
const glassCard = {
  background: "rgba(255, 255, 255, 0.04)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  borderRadius: "20px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
};

const glassCardHover = {
  background: "rgba(255, 255, 255, 0.07)",
  border: "1px solid var(--hover-border-color)",
  boxShadow: "0 8px 40px rgba(133, 0, 0, 0.15)",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}`;
};

const getInitials = (name) => {
  if (!name) return "?";
  return name.charAt(0).toUpperCase();
};

const profileColors = [
  "linear-gradient(135deg, #850000 0%, #c0392b 100%)",
  "linear-gradient(135deg, #1a237e 0%, #3f51b5 100%)",
  "linear-gradient(135deg, #006064 0%, #00acc1 100%)",
  "linear-gradient(135deg, #4a148c 0%, #9c27b0 100%)",
];

// ============================================================================
// UserLimitView — Session management before profile selection
// ============================================================================

/**
 * Parse a device string (user-agent or app identifier) into a friendly label + icon.
 */
function parseDevice(deviceStr) {
  if (!deviceStr || typeof deviceStr !== "string") {
    return { label: "دستگاه ناشناخته", icon: "💻" };
  }

  // Android app: "Manabakh-Android/16 (sdk_gphone64_arm64; Android 36)"
  const androidAppMatch = deviceStr.match(
    /Manabakh-Android\/(\d+)\s*\(([^;]*);?\s*Android\s*([\d.]+)\)/i
  );
  if (androidAppMatch) {
    const model = androidAppMatch[2]?.trim() || "";
    const androidVer = androidAppMatch[3] || "";
    return {
      label: `اپلیکیشن اندروید${model ? ` · ${model}` : ""}`,
      sub: androidVer ? `Android ${androidVer}` : "",
      icon: "📱",
    };
  }

  // TV Device
  if (/^TV Device$/i.test(deviceStr.trim())) {
    return { label: "دستگاه تلویزیون", sub: "", icon: "📺" };
  }

  // Browser user-agent
  const isWindows = /Windows/i.test(deviceStr);
  const isMac = /Macintosh|Mac OS/i.test(deviceStr);
  const isLinux = /Linux/i.test(deviceStr) && !/Android/i.test(deviceStr);
  const isAndroid = /Android/i.test(deviceStr);
  const isiOS = /iPhone|iPad/i.test(deviceStr);

  let os = "سیستم ناشناخته";
  let osIcon = "💻";
  if (isWindows) { os = "ویندوز"; osIcon = "🖥️"; }
  else if (isMac) { os = "مک"; osIcon = "💻"; }
  else if (isLinux) { os = "لینوکس"; osIcon = "🐧"; }
  else if (isAndroid) { os = "اندروید"; osIcon = "📱"; }
  else if (isiOS) { os = "iOS"; osIcon = "📱"; }

  // Extract browser name
  let browser = "";
  if (/Edg\//i.test(deviceStr)) browser = "Edge";
  else if (/OPR\//i.test(deviceStr) || /Opera/i.test(deviceStr)) browser = "Opera";
  else if (/Firefox\//i.test(deviceStr)) browser = "Firefox";
  else if (/Chrome\//i.test(deviceStr) && !/Chromium/i.test(deviceStr)) browser = "Chrome";
  else if (/Safari\//i.test(deviceStr) && !/Chrome/i.test(deviceStr)) browser = "Safari";

  return {
    label: `مرورگر${browser ? ` ${browser}` : ""}`,
    sub: os,
    icon: osIcon,
  };
}

function formatSessionTime(epoch) {
  if (!epoch) return "";
  try {
    return new Date(epoch).toLocaleDateString("fa-IR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export const UserLimitView = ({
  sessions,
  sessionLoading,
  onDeleteSession,
  deleteLoading,
}) => {
  return (
    <div className="w-full flex flex-col items-center gap-6 min-h-[400px] px-4 py-8">
      {/* Header */}
      <div className="flex flex-col items-center gap-2 text-center">
        <div
          className="flex items-center justify-center w-14 h-14 rounded-full mb-2"
          style={{
            background: "rgba(133, 0, 0, 0.2)",
            border: "1px solid rgba(133, 0, 0, 0.4)",
          }}
        >
          <span className="text-2xl">⚠️</span>
        </div>
        <h3 className="text-primary-white text-lg sm:text-xl font-bold">
          حداکثر تعداد نشست‌های فعال
        </h3>
        <p className="text-[#8C8C8C] text-sm sm:text-base max-w-[400px]">
          شما به حداکثر تعداد نشست‌های فعال رسیده‌اید. برای ادامه، یکی از
          دستگاه‌ها را خارج کنید.
        </p>
      </div>

      {/* Session list */}
      <div className="w-full max-w-[480px] flex flex-col gap-3">
        {sessionLoading ? (
          <div className="flex justify-center items-center py-8">
            <Spinner color="danger" />
          </div>
        ) : (
          sessions?.map((session) => {
            const id = session.sessionId || session._id || session.id;
            const device = parseDevice(session.device);
            const lastActive = formatSessionTime(
              session.lastActivity || session.createdAt
            );

            return (
              <div
                key={id}
                className="flex items-center justify-between px-4 py-3 rounded-2xl transition-all duration-300"
                style={{
                  ...glassCard,
                  borderRadius: "16px",
                  ...(session.isCurrent
                    ? {
                      border: "1px solid rgba(133, 0, 0, 0.35)",
                      background: "rgba(133, 0, 0, 0.06)",
                    }
                    : {}),
                }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {/* Device icon */}
                  <div
                    className="flex items-center justify-center w-10 h-10 rounded-full shrink-0"
                    style={{
                      background: "rgba(255, 255, 255, 0.06)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <span className="text-lg">{device.icon}</span>
                  </div>

                  {/* Info */}
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-primary-white text-sm font-medium truncate">
                        {device.label}
                      </span>
                      {session.isCurrent && (
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full shrink-0"
                          style={{
                            background: "rgba(133, 0, 0, 0.3)",
                            color: "#f5a8a8",
                            border: "1px solid rgba(133, 0, 0, 0.4)",
                          }}
                        >
                          نشست فعلی
                        </span>
                      )}
                    </div>
                    <span className="text-[#8C8C8C] text-xs truncate">
                      {device.sub}
                      {session.ip ? `${device.sub ? " · " : ""}${session.ip}` : ""}
                    </span>
                    {lastActive && (
                      <span className="text-[#666] text-[10px] mt-0.5">
                        {lastActive}
                      </span>
                    )}
                  </div>
                </div>

                {/* Delete button — hide for current session */}
                {!session.isCurrent ? (
                  <Button
                    size="sm"
                    variant="flat"
                    color="danger"
                    isLoading={deleteLoading === id}
                    className="min-w-[60px] text-xs rounded-xl shrink-0 mr-2"
                    onClick={() => onDeleteSession(id)}
                  >
                    خروج
                  </Button>
                ) : (
                  <span className="text-[#666] text-[10px] shrink-0 mr-2">
                    این دستگاه
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SelectProfileView — Glassmorphism redesign with max-profile removal modal
// ============================================================================
export const SelectProfileView = ({
  userListLoading,
  userList,
  selectProfileLoading,
  goToAddView,
  handleSelectProfile,
  onDeleteProfile,
  deleteProfileLoading,
}) => {
  const [hoveredId, setHoveredId] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const prevUserListLength = useRef(userList?.length);

  // When a profile is deleted from the modal and list shrinks below max,
  // auto-close modal and navigate to add-profile view
  useEffect(() => {
    if (
      showRemoveModal &&
      prevUserListLength.current >= MAX_PROFILES &&
      userList?.length < MAX_PROFILES
    ) {
      setShowRemoveModal(false);
      goToAddView();
    }
    prevUserListLength.current = userList?.length;
  }, [userList?.length, showRemoveModal, goToAddView]);

  const handleAddClick = () => {
    if (userList?.length >= MAX_PROFILES) {
      setShowRemoveModal(true);
    } else {
      goToAddView();
    }
  };

  return (
    <div className="w-full flex flex-col items-center gap-8 min-h-[400px] px-4 py-8">
      <div className="flex flex-col items-center gap-1">
        <h3 className="text-primary-white text-xl sm:text-2xl font-bold">
          چه کسی تماشا میکند؟
        </h3>
        <p className="text-[#8C8C8C] text-sm">پروفایل خود را انتخاب کنید</p>
      </div>

      {userListLoading ? (
        <div className="flex justify-center items-center py-12">
          <Spinner color="danger" />
        </div>
      ) : (
        <div className="flex flex-wrap justify-center gap-5 w-full max-w-[500px]">
          {/* Profile cards */}
          {userList?.map((user, index) => (
            <button
              key={user._id}
              disabled={selectProfileLoading}
              className="flex flex-col items-center gap-3 p-5 cursor-pointer transition-all duration-300 outline-none border-none bg-transparent group"
              style={{
                ...glassCard,
                minWidth: "130px",
                maxWidth: "160px",
                flex: "1 1 130px",
                ...(hoveredId === user._id ? glassCardHover : {}),
              }}
              onClick={() => handleSelectProfile(user._id)}
              onMouseEnter={() => setHoveredId(user._id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg transition-transform duration-300 group-hover:scale-110"
                style={{
                  background: profileColors[index % profileColors.length],
                }}
              >
                {getInitials(user.fullName)}
              </div>
              <span className="text-primary-white text-sm font-medium text-center">
                {user.fullName}
              </span>
            </button>
          ))}

          {/* Add profile card — always visible */}
          <button
            disabled={selectProfileLoading}
            className="flex flex-col items-center gap-3 p-5 cursor-pointer transition-all duration-300 outline-none bg-transparent"
            style={{
              ...glassCard,
              minWidth: "130px",
              maxWidth: "160px",
              flex: "1 1 130px",
              border: "1px dashed rgba(255, 255, 255, 0.15)",
              ...(hoveredId === "add"
                ? {
                  border: "1px dashed rgba(133, 0, 0, 0.5)",
                  background: "rgba(133, 0, 0, 0.06)",
                }
                : {}),
            }}
            onClick={handleAddClick}
            onMouseEnter={() => setHoveredId("add")}
            onMouseLeave={() => setHoveredId(null)}
          >
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-3xl transition-transform duration-300 hover:scale-110"
              style={{
                background: "rgba(255, 255, 255, 0.06)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: "rgba(255, 255, 255, 0.5)",
              }}
            >
              +
            </div>
            <span className="text-[#8C8C8C] text-sm font-medium">
              عضو جدید
            </span>
          </button>
        </div>
      )}

      {selectProfileLoading && (
        <div className="flex items-center gap-2 text-[#8C8C8C] text-sm">
          <Spinner size="sm" color="danger" />
          <span>در حال ورود...</span>
        </div>
      )}

      {/* ---- Max-profile removal modal ---- */}
      <Modal
        isOpen={showRemoveModal}
        onOpenChange={setShowRemoveModal}
        placement="center"
        classNames={{
          base: "bg-[#1a1a1a] border border-[rgba(255,255,255,0.08)]",
          header: "border-b border-[rgba(255,255,255,0.06)]",
          body: "py-4",
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <span className="text-primary-white text-lg font-bold">
              حداکثر تعداد پروفایل‌ها
            </span>
          </ModalHeader>
          <ModalBody>
            <p className="text-[#8C8C8C] text-sm mb-4 leading-6">
              برای افزودن پروفایل جدید ابتدا یکی از پروفایل‌های زیر را حذف
              کنید.
            </p>

            <div className="flex flex-col gap-3">
              {userList?.map((profile, index) => {
                const isFirst = index === 0;
                const id = profile._id;
                return (
                  <div
                    key={id}
                    className="flex items-center justify-between px-4 py-3 rounded-2xl transition-all duration-300"
                    style={{
                      ...glassCard,
                      borderRadius: "14px",
                    }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                        style={{
                          background:
                            profileColors[index % profileColors.length],
                        }}
                      >
                        {getInitials(profile.fullName)}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-primary-white text-sm font-medium truncate">
                          {profile.fullName}
                        </span>
                        {isFirst && (
                          <span className="text-[#666] text-[10px]">
                            پروفایل اصلی — غیرقابل حذف
                          </span>
                        )}
                      </div>
                    </div>

                    {!isFirst && (
                      <Button
                        size="sm"
                        variant="flat"
                        color="danger"
                        isLoading={deleteProfileLoading === id}
                        className="min-w-[60px] text-xs rounded-xl shrink-0 mr-2"
                        onClick={() => onDeleteProfile(id)}
                      >
                        حذف
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

// ============================================================================
// AddProfile — Glassmorphism redesign with optional photo upload
// ============================================================================
export const AddProfile = ({
  username,
  setUsername,
  handleAddUser,
  addProfileLoading,
}) => {
  const [ageRange, setAgeRange] = useState(120);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const clearImage = (e) => {
    e.stopPropagation();
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="w-full flex flex-col items-center gap-6 min-h-[400px] px-4 py-8">
      <div className="flex flex-col items-center gap-1">
        <h3 className="text-primary-white text-xl sm:text-2xl font-bold">
          افزودن عضو جدید
        </h3>
        <p className="text-[#8C8C8C] text-sm">اطلاعات عضو جدید را وارد کنید</p>
      </div>

      {/* Glass form card */}
      <div
        className="w-full max-w-[480px] flex flex-col gap-6 p-6 sm:p-8"
        style={glassCard}
      >
        {/* Profile photo upload */}
        <div className="flex flex-col items-center gap-2">
          <label className="text-[#8C8C8C] text-xs mb-1 block">
            تصویر پروفایل (اختیاری)
          </label>
          <div
            className="relative w-20 h-20 rounded-full cursor-pointer transition-all duration-300 hover:scale-105 group"
            style={{
              background: imagePreview
                ? `url(${imagePreview}) center/cover no-repeat`
                : "rgba(255, 255, 255, 0.06)",
              border: imagePreview
                ? "2px solid rgba(133, 0, 0, 0.5)"
                : "2px dashed rgba(255, 255, 255, 0.15)",
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            {!imagePreview && (
              <div className="flex items-center justify-center w-full h-full">
                <span
                  className="text-2xl transition-colors duration-200"
                  style={{ color: "rgba(255, 255, 255, 0.4)" }}
                >
                  📷
                </span>
              </div>
            )}
            {imagePreview && (
              <button
                type="button"
                onClick={clearImage}
                className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 border-none cursor-pointer"
              >
                ✕
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleImageSelect}
          />
        </div>

        {/* Name input */}
        <div>
          <label className="text-[#8C8C8C] text-xs mb-2 block pr-1">
            نام کاربر
          </label>
          <Input
            placeholder="نام"
            size="lg"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            classNames={{
              inputWrapper:
                "!bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] hover:border-[var(--hover-border-color)] h-[56px] rounded-2xl",
              input: "!text-primary-white text-base",
            }}
            className="text-primary-white"
          />
        </div>

        {/* Age range tabs */}
        <div>
          <label className="text-[#8C8C8C] text-xs mb-3 block pr-1">
            رده سنی
          </label>
          <Tabs
            aria-label="Age options"
            color="primary"
            variant="bordered"
            className="w-full justify-center"
            classNames={{
              tabList:
                "w-full justify-around border border-solid border-[rgba(255,255,255,0.1)] rounded-2xl relative p-0 bg-[rgba(255,255,255,0.03)]",
              cursor: "w-full bg-transparent",
              tab: "w-full flex justify-center p-0 h-[48px]",
              tabContent:
                "group-data-[selected=true]:bg-[rgba(255,255,255,0.1)] group-data-[selected=true]:text-white text-[#8C8C8C] justify-center w-full h-full rounded-xl transition-all",
              panel: "w-full pt-4",
            }}
          >
            <Tab
              onClick={() => setAgeRange(120)}
              title={
                <div className="flex justify-center w-full h-full items-center">
                  <span className="text-xs sm:text-sm font-medium">
                    بزرگسال
                  </span>
                </div>
              }
            ></Tab>
            <Tab
              title={
                <div className="flex justify-center w-full h-full items-center">
                  <span className="text-xs sm:text-sm font-medium">
                    با نظارت والدین
                  </span>
                </div>
              }
            >
              <RadioGroup
                orientation="horizontal"
                className="flex justify-between items-center w-full"
                classNames={{
                  wrapper: "w-full justify-around gap-2",
                }}
                onChange={(e) => {
                  setAgeRange(e.target.value);
                }}
              >
                <Radio
                  classNames={{ label: "text-primary-white text-sm" }}
                  value={7}
                >
                  تا ۷ سال
                </Radio>
                <Radio
                  classNames={{ label: "text-primary-white text-sm" }}
                  value={13}
                >
                  تا ۱۳ سال
                </Radio>
                <Radio
                  classNames={{ label: "text-primary-white text-sm" }}
                  value={18}
                >
                  تا ۱۸ سال
                </Radio>
              </RadioGroup>
            </Tab>
          </Tabs>
        </div>

        {/* Submit */}
        <Button
          size="lg"
          fullWidth
          color="primary"
          isLoading={addProfileLoading}
          className="text-primary-white font-bold text-base rounded-2xl min-h-[50px]"
          onClick={() => handleAddUser(username, ageRange, imageFile)}
        >
          ایجاد و ورود
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// AgeRatingSelectionView (unchanged)
// ============================================================================
export const AgeRatingSelectionView = ({ handleAgeRating }) => {
  return (
    <div className="min-h-[400px]">
      <h4 className="text-primary-white text-base sm:text-xl md:text-2xl lg:text-3xl font-bold">
        رده سنی خود را انتخاب کنید
      </h4>
      <div className="w-full">
        <Tabs
          aria-label="Options"
          color="primary"
          variant="bordered"
          className="mb-12 w-full justify-center"
          classNames={{
            tabList:
              "xs:w-[350px] sm:w-[400px] md:w-[450px] lg:w-[500px] justify-around border-2 border-solid border-[#BFBFBF] rounded-xl relative p-0",
            cursor: "w-full bg-transparent",
            tab: "w-full flex justify-center p-0 h-[50px]",
            tabContent:
              " group-data-[selected=true]:bg-[#BFBFBF] group-data-[selected=true]:text-[#000000] text-primary-white justify-center w-full h-full",
            panel: "w-full",
          }}
        >
          <Tab
            title={
              <div className="flex justify-center w-full h-full items-center">
                <span className="text-sm xs:text-base sm:text-[22px] font-medium">
                  بزرگسال هستم
                </span>
              </div>
            }
          ></Tab>
          <Tab
            title={
              <div className="flex justify-center w-full h-full items-center">
                <span className="text-sm xs:text-base sm:text-[22px] font-medium">
                  با نظارت والدین
                </span>
              </div>
            }
          >
            <div className="w-full">
              <RadioGroup
                orientation="horizontal"
                className="flex justify-between items-center w-full mb-4"
                classNames={{
                  wrapper: "w-full justify-around",
                }}
              >
                <Radio
                  classNames={{
                    label: "text-primary-white",
                  }}
                  value="buenos-aires"
                >
                  تا ۷ سال
                </Radio>
                <Radio
                  classNames={{
                    label: "text-primary-white",
                  }}
                  value="sydney"
                >
                  تا ۱۳ سال
                </Radio>
                <Radio
                  classNames={{
                    label: "text-primary-white",
                  }}
                  value="san-francisco"
                >
                  تا ۱۸ سال
                </Radio>
              </RadioGroup>
            </div>
          </Tab>
        </Tabs>
        <CustomButton
          size="xl"
          fullWidth
          label="تایید"
          color="primary"
          textColor="text-primary-white"
          classNames="text-lg"
          onClick={handleAgeRating}
        />
      </div>
    </div>
  );
};

// ============================================================================
// OTPView (unchanged)
// ============================================================================
export const OTPView = ({
  phoneNumber,
  otpTimer,
  otpErr,
  handleOtp,
  setLoginStage,
  loginStage,
  handleLogin,
  loginLoading,
  otpResetKey,
}) => {
  return (
    <div className="w-full h-full flex flex-col justify-center items-center gap-9 min-h-[400px]">
      <div className="flex justify-center items-center pt-5">
        <div className="hidden md:flex">
          <SmsIcon />
        </div>
        <h4 className="mr-2 text-xl lg:text-2xl">
          کد تایید به شماره <span className="font-bold">{phoneNumber}</span>{" "}
          ارسال شد
        </h4>
      </div>
      {otpTimer !== 0 && (
        <div className="flex justify-center items-center">
          <span
            onClick={(e) => setLoginStage(0)}
            className="text-base md:text-lg lg:text-xl font-normal text-[#BFBFBF] cursor-pointer"
          >
            شماره اشتباه وارد کردم
          </span>
          <LeftArrowIcon />
        </div>
      )}
      <div className="flex items-center justify-center py-2">
        <OtpInput
          key={otpResetKey}
          length={5}
          error={otpErr}
          apiCallback={handleOtp}
          watchValue={loginStage === 1}
        />
      </div>
      {otpTimer !== 0 ? (
        <div className="flex justify-center items-center ">
          <span className="mr-2 w-[60px] text-base md:text-xl lg:text-2xl">
            {formatTime(otpTimer)}
          </span>
          <div className="flex justify-center items-center">
            <HourglassIcon />
          </div>
        </div>
      ) : (
        <div className="flex w-full justify-center items-center gap-3">
          <CustomButton
            size="lg"
            label="ویرایش شماره"
            color="primary"
            textColor="text-primary-white"
            rightIcon={<EditIcon />}
            classNames="text-base mb-2 font-semibold min-h-[40px] flex-fill max-w-[200px]"
            onClick={() => setLoginStage(0)}
          />
          <CustomButton
            size="lg"
            label="ارسال مجدد"
            color="primary"
            textColor="text-primary-white bg-[#262626]"
            rightIcon={<ResendIcon />}
            classNames="text-base mb-2 font-semibold min-h-[40px] flex-fill max-w-[200px]"
            onClick={() => handleLogin(true)}
            isLoading={loginLoading}
          />
        </div>
      )}
      <div
        className={`flex justify-start items-center pb-3 text-[#DA8A8A] text-sm md:text-base lg:text-lg
        ${otpErr ? "!visible" : "invisible"}`}
      >
        <LoginErrorIcon />
        <span>کد وارد شده اشتباه است</span>
      </div>
    </div>
  );
};

// ============================================================================
// PasswordView (unchanged)
// ============================================================================
export const PasswordView = ({
  password,
  handlePassword,
  isVisible,
  toggleVisibility,
  handlePasswordLogin,
  loginLoading,
  onBack,
}) => {
  return (
    <form
      className="min-h-[400px]"
      onSubmit={(event) => {
        event.preventDefault();
        handlePasswordLogin();
      }}
    >
      <h4 className="text-primary-white text-[28px] font-bold">
        رمز خود را وارد نمایید
      </h4>
      <div className="w-full">
        <Input
          placeholder="رمز"
          size="lg"
          value={password}
          onChange={handlePassword}
          classNames={{
            inputWrapper: "h-[90px]",
            label: "text-xl text-[#8C8C8C]",
            input: "!text-primary-white text-xl",
          }}
          className="input-button h-[60px] text-primary-white mb-6 !text-3xl"
          type={isVisible ? "text" : "password"}
          endContent={
            <button
              className={`focus:outline-none 
            ${isVisible ? "opacity-50" : ""}`}
              type="button"
              onClick={toggleVisibility}
            >
              <ShowPasswordIcon />
            </button>
          }
        />
        <CustomButton
          size="xl"
          fullWidth
          label="ورود"
          color="primary"
          textColor="text-primary-white"
          classNames="text-lg"
          type="submit"
          isLoading={loginLoading}
        />
        <button
          type="button"
          className="w-full mt-4 text-[#BFBFBF] text-sm"
          onClick={onBack}
          disabled={loginLoading}
        >
          بازگشت و ورود با کد یک‌بارمصرف
        </button>
      </div>
    </form>
  );
};

// ============================================================================
// LoginView (unchanged)
// ============================================================================
export const LoginView = ({
  phoneNumber,
  handlePhoneNumber,
  handleKeyPress,
  handleLogin,
  goToPassword,
  loginLoading,
  loginErr,
}) => {
  return (
    <div className="flex flex-col justify-center items-center w-full min-h-[400px]">
      <div className="flex w-full">
        {/* Phone Login Section */}
        <div className="w-full">
          <form
            onSubmit={(e) => {
              e?.preventDefault();
              handleLogin(false);
            }}
            className="flex flex-col justify-center items-center gap-3 w-full px-2"
          >
            <h4 className="text-primary-white mb-3 text-base sm:text-xl md:text-2xl lg:text-xl font-bold">
              شماره موبایل خود را وارد کنید
            </h4>
            <div className="w-full">
              <Input
                fullWidth
                placeholder="شماره موبایل"
                size="lg"
                inputMode="numeric"
                value={phoneNumber}
                onChange={handlePhoneNumber}
                onKeyDown={handleKeyPress}
                classNames={{
                  inputWrapper:
                    "h-[90px] group-data-[focus-visible=true]:ring-0 group-data-[focus-visible=true]:ring-offset-0",
                  label: "text-base md:text-lg lg:text-xl text-[#8C8C8C]",
                  input:
                    "!text-primary-white text-base md:text-lg lg:text-xl !appearance-none",
                }}
                className="input-button h-[60px] text-primary-white !text-3xl"
                startContent={<PhoneNumberIcon />}
              />
              <div
                className={`flex justify-start items-center text-[#DA8A8A] gap-3 text-sm md:text-base lg:text-lg
              ${loginErr ? "!visible py-3" : "invisible"}`}
              >
                <LoginErrorIcon />
                <span>شماره موبایل معتبر نیست</span>
              </div>
              <Button
                size="xl"
                fullWidth
                color="primary"
                isLoading={loginLoading}
                className="text-lg min-h-[50px] mb-3 text-primary-white font-bold md:text-base rounded-full"
                type="submit"
              >
                دریافت کد تأیید
              </Button>
              {/* <button
                type="button"
                className="text-[#BFBFBF] text-sm py-2"
                onClick={goToPassword}
                disabled={loginLoading}
              >
                ورود با رمز عبور
              </button> */}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
